import { Link } from "react-router-dom";
import { ArrowRight, Battery, Gauge, Leaf, Zap } from "lucide-react";

const HERO_IMG = "/media/hero.jpg";

export default function Home() {

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden isolate h-[560px] md:h-[640px] lg:h-[720px]">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMG}
            alt="Ride the future"
            className="h-full w-full object-cover object-[center_35%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/25 via-transparent to-white/10" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white via-white/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-14 lg:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end">
            <div className="lg:col-span-8 animate-rise">
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/models"
                  data-testid="hero-cta-explore"
                  className="group inline-flex items-center gap-2 rounded-full bg-slate-950 text-white px-7 py-3.5 text-sm font-medium hover:bg-sky-600 transition-colors"
                >
                  Explore the lineup
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-cta-about"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/60 backdrop-blur-md px-7 py-3.5 text-sm font-medium text-slate-900 hover:border-slate-950"
                >
                  Our story
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 animate-rise" style={{ animationDelay: "120ms" }}>
              <div className="rounded-3xl border border-white/60 bg-white/70 backdrop-blur-2xl backdrop-saturate-150 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
                <p className="text-[11px] uppercase tracking-[0.22em] text-sky-600 font-semibold">
                  At a glance
                </p>
                <div className="mt-5 grid grid-cols-2 gap-4">
                  {[
                    { Icon: Gauge, label: "Top speed", value: "25 km/h" },
                    { Icon: Battery, label: "Up to", value: "170 km range" },
                    { Icon: Zap, label: "Charge", value: "3–5 hrs" },
                    { Icon: Leaf, label: "Emission", value: "Zero" },
                  ].map(({ Icon, label, value }) => (
                    <div key={label} className="rounded-2xl bg-white p-4 border border-slate-100">
                      <Icon className="h-4 w-4 text-sky-600" />
                      <p className="mt-3 font-heading text-lg text-slate-950">{value}</p>
                      <p className="text-[11px] text-slate-500 uppercase tracking-[0.15em]">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="border-y border-slate-200 bg-white/50 backdrop-blur-md overflow-hidden">
          <div className="animate-marquee flex gap-12 py-4 whitespace-nowrap font-mono text-xs uppercase tracking-[0.28em] text-slate-500">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex gap-12">
                <span>• 6 models</span>
                <span>• License-free under 25 km/h</span>
                <span>• Lithium-ion batteries</span>
                <span>• Pan-India service</span>
                <span>• Zero tailpipe emission</span>
                <span>• Made in India</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="home-mission">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.22em] text-sky-600 font-semibold">
              Why chase petrol?
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-slate-950 font-medium">
              Plug In. Ride Out. Repeat.
            </h2>
            <p className="mt-6 text-slate-700 leading-relaxed">
              The future of everyday mobility isn&apos;t faster — it&apos;s smarter.
              With India&apos;s growing focus on electric transportation and
              sustainable mobility, switching to an EV has never been easier.
              Charge at home, spend less on every ride, and enjoy quiet, reliable
              transportation designed for the way India really moves.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-4">
            {[
              { k: "0", v: "Tailpipe emissions" },
              { k: "₹0.20", v: "Per km running cost" },
              { k: "Up to 170 km", v: "Range" },
              { k: "NO", v: "License required" },
            ].map((s) => (
              <div
                key={s.v}
                className="rounded-2xl border border-slate-200 bg-white p-6"
              >
                <p className="font-heading text-3xl text-slate-950">{s.k}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
